import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { onboardingApi } from '../services/onboardingApi';

const OnboardingContext = createContext();

// Storage key for caching onboarding status locally
const ONBOARDING_STATUS_STORAGE_KEY = 'onboardingStatus';

// Stable empty-object reference for the guest case (onboardingStatus is
// null) — `onboardingStatus?.onboarding_state || {}` would otherwise create
// a brand-new {} literal every render. For a logged-out/guest visitor
// onboardingStatus never becomes a real object, so every re-render of
// OnboardingProvider (route changes, unrelated parent state updates, etc.)
// handed consumers a new onboardingState reference. Any effect elsewhere in
// the app that depends on onboardingState (e.g. the Keyword Explorer
// onboarding tour) re-ran on every one of those renders and reset itself
// back to its initial step — making Skip/Next look completely unresponsive
// for guests, while logged-in users (whose onboardingStatus object is only
// replaced on an actual fetch) never hit this.
const EMPTY_ONBOARDING_STATE = {};

// Merge backend / cached onboarding status with any sessionStorage tutorial flags
const mergeWithSessionTutorialFlags = (status) => {
  if (!status || typeof status !== 'object') return status;

  const merged = { ...status };
  const state = { ...(status.onboarding_state || {}) };

  try {
    const keywordSeen = sessionStorage.getItem('keywordExplorerOnboardingSeen') === 'true';
    const appTrackingSeen = sessionStorage.getItem('appTrackingOnboardingSeen') === 'true';

    if (keywordSeen) {
      state.keyword_explorer = true;
    }
    if (appTrackingSeen) {
      state.app_tracking = true;
    }
  } catch (e) {
    // Ignore storage errors – status from backend still valid
  }

  merged.onboarding_state = state;
  return merged;
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

/**
 * Centralized Onboarding Provider
 * 
 * Manages onboarding state across the entire app:
 * - Checks onboarding status on mount and route changes
 * - Gates pages based on onboarding_state flags
 * - Provides methods to mark pages as completed
 * 
 * Expected backend response:
 * {
 *   "is_onboarded": boolean | null,
 *   "onboarding_state": {
 *     "ai_screen": boolean,
 *     "trial_activated": boolean,
 *     "keyword_explorer": boolean,
 *     "category_analysis": boolean,
 *     "publisher_analysis": boolean,
 *     // ... other pages
 *   }
 * }
 */
export const OnboardingProvider = ({ children }) => {
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch onboarding status from backend
  const fetchOnboardingStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const rawStatus = await onboardingApi.getStatus();
      const status = mergeWithSessionTutorialFlags(rawStatus);

      setOnboardingStatus(status);

      // Persist latest status to both localStorage and sessionStorage for reuse
      try {
        const serialized = JSON.stringify(status);
        localStorage.setItem(ONBOARDING_STATUS_STORAGE_KEY, serialized);
        sessionStorage.setItem(ONBOARDING_STATUS_STORAGE_KEY, serialized);
      } catch (e) {
        // Ignore storage errors – should not block app
      }

      return status;
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
      // On error, allow access (fail open) - don't block users
      setOnboardingStatus(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if a specific page is allowed
  const isPageAllowed = useCallback((pageKey) => {
    if (!onboardingStatus) return true; // Fail open - allow if status unknown

    const { is_onboarded, onboarding_state = {} } = onboardingStatus;

    // If fully onboarded, all pages are allowed
    if (is_onboarded === true) return true;

    // Special handling for AI screen - must be completed first
    if (pageKey !== 'ai_screen' && pageKey !== 'chatbot') {
      if (!onboarding_state.ai_screen) {
        return false; // Must complete AI screen first
      }
    }

    // Check if this specific page is allowed
    // Pages are allowed if:
    // 1. They're explicitly marked as true in onboarding_state
    // 2. OR is_onboarded is null (first time user - allow first page)
    // 3. OR is_onboarded is false but page is not in onboarding_state (new page, allow)
    return onboarding_state[pageKey] === true || 
           is_onboarded === null || 
           onboarding_state[pageKey] === undefined;
  }, [onboardingStatus]);

  // Mark a page as completed in onboarding
  const markPageCompleted = useCallback(async (pageKey, additionalState = {}) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const currentState = onboardingStatus?.onboarding_state || {};
      const updatedState = {
        ...currentState,
        [pageKey]: true,
        ...additionalState,
      };

      // Determine is_onboarded value
      // If this is the AI screen, set is_onboarded to true
      // Otherwise, keep existing is_onboarded value
      const is_onboarded = pageKey === 'ai_screen' ? true : onboardingStatus?.is_onboarded;

      const response = await onboardingApi.updateOnboarding({
        is_onboarded,
        onboarding_state: updatedState,
      });

      // Build the new status object and update local state + caches
      const newStatus = mergeWithSessionTutorialFlags({
        ...(onboardingStatus || {}),
        is_onboarded,
        onboarding_state: updatedState,
      });

      setOnboardingStatus(newStatus);

      try {
        const serialized = JSON.stringify(newStatus);
        localStorage.setItem(ONBOARDING_STATUS_STORAGE_KEY, serialized);
        sessionStorage.setItem(ONBOARDING_STATUS_STORAGE_KEY, serialized);
      } catch (e) {
        // Ignore storage errors
      }
    } catch (error) {
      console.error(`Error marking page ${pageKey} as completed:`, error);
      // Don't throw - allow UI to continue
    }
  }, [onboardingStatus]);

  // Check if user should be redirected (e.g., to AI screen first)
  const shouldRedirectToOnboarding = useCallback(() => {
    // Never run onboarding redirects when user is logged out
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    if (!onboardingStatus) return false;

    const { is_onboarded, onboarding_state = {} } = onboardingStatus;

    // If fully onboarded, no redirect needed
    if (is_onboarded === true) return false;

    // Check if user just dismissed the AI screen (don't redirect back immediately)
    const aiScreenDismissed = sessionStorage.getItem('ai_screen_dismissed') === 'true';
    if (aiScreenDismissed) {
      // Clear the flag after a short delay to allow navigation
      setTimeout(() => {
        try {
          sessionStorage.removeItem('ai_screen_dismissed');
        } catch (e) {}
      }, 1000);
      return false; // Don't redirect if user just dismissed
    }

    // If AI screen not completed, redirect to chatbot
    if (!onboarding_state.ai_screen && location.pathname !== '/chatbot') {
      return '/chatbot';
    }

    return false;
  }, [onboardingStatus, location.pathname]);

  // Fetch status on mount and when route changes
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // First, try to hydrate state from localStorage or sessionStorage
      try {
        const cached =
          localStorage.getItem(ONBOARDING_STATUS_STORAGE_KEY) ||
          sessionStorage.getItem(ONBOARDING_STATUS_STORAGE_KEY);

        if (cached) {
          const parsed = JSON.parse(cached);
          const merged = mergeWithSessionTutorialFlags(parsed);

          // Basic shape validation: ensure it looks like the expected response
          if (merged && typeof merged === 'object' && 'onboarding_state' in merged) {
            setOnboardingStatus(merged);
            // We can consider this as "loaded" for UI purposes, even while we refresh from API
            setIsLoading(false);
          }
        }
      } catch (e) {
        // Ignore cache errors and fall through to API fetch
      }

      // Always refresh from backend to keep status accurate
      fetchOnboardingStatus();
    } else {
      // If there's no token, ensure we clear any stale onboarding state
      setOnboardingStatus(null);
      setIsLoading(false);

      try {
        localStorage.removeItem(ONBOARDING_STATUS_STORAGE_KEY);
        sessionStorage.removeItem(ONBOARDING_STATUS_STORAGE_KEY);
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [fetchOnboardingStatus, location.pathname]);

  // Handle redirects for onboarding flow
  useEffect(() => {
    if (isLoading) return;

    const redirectPath = shouldRedirectToOnboarding();
    if (redirectPath) {
      navigate(redirectPath, { 
        state: { 
          initialPrompt: 'Welcome me as a new user on keyquest and give a brief description about the platform?',
          fromOnboarding: true 
        } 
      });
    }
  }, [isLoading, shouldRedirectToOnboarding, navigate]);

  // Memoized so consumers only see a new `value` (and a new `onboardingState`
  // reference) when something actually changed — not on every incidental
  // re-render of this provider, which otherwise re-triggers any effect
  // downstream that depends on onboardingState (see EMPTY_ONBOARDING_STATE
  // above for why this mattered most for guests).
  const value = useMemo(() => ({
    onboardingStatus,
    isLoading,
    isPageAllowed,
    markPageCompleted,
    fetchOnboardingStatus,
    isOnboarded: onboardingStatus?.is_onboarded === true,
    onboardingState: onboardingStatus?.onboarding_state || EMPTY_ONBOARDING_STATE,
  }), [onboardingStatus, isLoading, isPageAllowed, markPageCompleted, fetchOnboardingStatus]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
